import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { urlValidationEndpoints } from '@/api/endpoints';
import { callGet } from '@/api/fetcher';
import { Datasource, DatasourceType } from '@/shared/types/collections';

import { isZendeskConfig } from '../helper';

export type ExternalSourceProvider = 'zendesk' | 'intercom' | 'workvivo' | 'gdrive';

export const PROVIDER_BY_TYPE: Partial<Record<DatasourceType, ExternalSourceProvider>> = {
  [DatasourceType.ZENDESK_KB]: 'zendesk',
  [DatasourceType.INTERCOM_KB]: 'intercom',
  [DatasourceType.WORKVIVO_KB]: 'workvivo',
  [DatasourceType.GDRIVE]: 'gdrive',
};

const PROVIDER_NAMES: Record<ExternalSourceProvider, string> = {
  zendesk: 'Zendesk',
  intercom: 'Intercom',
  workvivo: 'Workvivo',
  gdrive: 'Google Drive',
};

const INTERCOM_URL = 'https://app.intercom.com';

const GOOGLE_DRIVE_URL = 'https://drive.google.com';

const SAFE_SUBDOMAIN = /^[a-z0-9-]{1,63}$/i;

const CATEGORIES_STALE_TIME = 5 * 60 * 1_000;

type ZendeskCategory = { id: string };
type ZendeskCategoriesResponse = { success: boolean; categories?: ZendeskCategory[] };

const getExternalSourceProvider = (datasource: Datasource | undefined) =>
  datasource ? PROVIDER_BY_TYPE[datasource.type] : undefined;

const getProviderUrl = (datasource: Datasource | undefined): string | undefined => {
  const provider = getExternalSourceProvider(datasource);

  if (provider === 'intercom') {
    return INTERCOM_URL;
  }
  if (provider === 'gdrive') {
    return GOOGLE_DRIVE_URL;
  }
  if (provider === 'zendesk' && isZendeskConfig(datasource.config)) {
    const { subdomain, locales } = datasource.config;
    if (!SAFE_SUBDOMAIN.test(subdomain ?? '')) {
      return undefined;
    }
    const locale = locales?.[0];
    return locale
      ? `https://${subdomain}.zendesk.com/hc/${encodeURIComponent(locale)}`
      : `https://${subdomain}.zendesk.com`;
  }
  return undefined;
};

interface ExternalSourceIssue {
  provider: ExternalSourceProvider | undefined;
  providerName: string | undefined;
  providerUrl: string | undefined;
  hasEditableCategories: boolean;
  hasMissingArticles: boolean;
  isCheckingCategories: boolean;
}

export const useExternalSourceIssue = (datasource: Datasource | undefined): ExternalSourceIssue => {
  const provider = getExternalSourceProvider(datasource);
  const config = datasource?.config;
  const zendeskConfig = provider === 'zendesk' && isZendeskConfig(config) ? config : undefined;

  const subdomain = zendeskConfig?.subdomain;
  const locale = zendeskConfig?.locales?.[0];
  const selectedCategories = zendeskConfig?.categories;

  const { data: availableIds, isLoading } = useQuery({
    queryKey: [urlValidationEndpoints.categories(subdomain, locale)],
    queryFn: (): Promise<ZendeskCategoriesResponse> =>
      callGet(urlValidationEndpoints.categories(subdomain, locale)),
    select: (response) => new Set(response.categories?.map((category) => category.id)),
    enabled: !!subdomain && !!locale && !!selectedCategories?.length,
    staleTime: CATEGORIES_STALE_TIME,
    retry: false,
  });

  const hasMissingArticles = useMemo(() => {
    if (!availableIds?.size || !selectedCategories?.length) {
      return false;
    }
    return selectedCategories.some((categoryId) => !availableIds.has(categoryId));
  }, [availableIds, selectedCategories]);

  return {
    provider,
    providerName: provider && PROVIDER_NAMES[provider],
    providerUrl: getProviderUrl(datasource),
    hasEditableCategories: provider === 'zendesk',
    hasMissingArticles,
    isCheckingCategories: isLoading,
  };
};
